import { Typography } from "@mui/material";
import { keyBy } from "lodash";
import { ReactElement } from "react";

import { ListAllActivePricingAssignmentsQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import Table from "~frontend/components/Table";
import { TableBox } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import { useTenantSlug } from "~frontend/providers/TenantSlugProvider";
import { palette } from "~frontend/theme";
import { getTenantHref } from "~frontend/util/tenantUrl";

export type TenantData =
  ListAllActivePricingAssignmentsQuery["listAllActivePricingAssignments"]["tenants"][number];
export type SubaccountData = TenantData["subaccountAssignments"][number];

export type ChangePricingTarget =
  | {
      kind: "tenant";
      tenantId: string;
      currentBundleId: string;
      tenantName: string;
    }
  | {
      kind: "subaccount";
      subaccountId: string;
      tenantId: string;
      currentBundleId: string;
      subaccountName: string;
      subaccountSlug: string;
      isWeText: boolean;
    };

type TenantRow = TenantData & {
  kind: "tenant";
  subaccountAssignments: SubaccountRow[];
};
type SubaccountRow = SubaccountData & {
  kind: "subaccount";
  parentTenantId: string;
};
type PricingAssignmentRow = TenantRow | SubaccountRow;

function toRows(tenants: TenantData[]): TenantRow[] {
  const byTenantId = keyBy(tenants, t => t.tenantId);
  return Object.values(byTenantId).map(tenant => {
    const bySubaccountId = keyBy(
      tenant.subaccountAssignments,
      sa => sa.subaccountId
    );
    return {
      ...tenant,
      kind: "tenant" as const,
      subaccountAssignments: Object.values(bySubaccountId).map(sa => ({
        ...sa,
        kind: "subaccount" as const,
        parentTenantId: tenant.tenantId,
      })),
    };
  });
}

function getChildRows(row: PricingAssignmentRow): PricingAssignmentRow[] {
  if (row.kind === "tenant") {
    return row.subaccountAssignments;
  }
  return [];
}

export default function PricingAssignmentsTable({
  tenants,
  totalCount,
  page,
  pageSize,
  onChangePage,
  onChangePageSize,
  onChangePricing,
}: {
  tenants: TenantData[];
  totalCount: number;
  page: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onChangePricing: (target: ChangePricingTarget) => void;
}): ReactElement {
  const currentTenantSlug = useTenantSlug();
  const rows = toRows(tenants);

  return (
    <TableBox>
      <Table<PricingAssignmentRow>
        columns={[
          {
            key: "tenant",
            title: "Tenant",
            renderValue: row => {
              if (row.kind === "tenant") {
                return (
                  <ExternalLink
                    href={getTenantHref(currentTenantSlug, row.tenantSlug)}
                  >
                    <Typography variant="body2">{row.tenantName}</Typography>
                  </ExternalLink>
                );
              }
              return (
                <FlexBox gap={1} alignItems="center">
                  {row.isWeText && (
                    <TagChip
                      label="WeText"
                      sx={{
                        borderRadius: 0,
                        fontWeight: 600,
                        color: palette.navy,
                        fontSize: "10px",
                      }}
                    />
                  )}
                  <Typography variant="body3">{row.subaccountName}</Typography>
                </FlexBox>
              );
            },
          },
          {
            key: "pricingOption",
            title: "Pricing Option",
            align: "left",
            renderValue: row => row.pricingBundleName,
          },
        ]}
        rows={rows}
        getChildRows={getChildRows}
        showRowActionsOnChildRows
        rowActions={row => [
          {
            id: "change-pricing",
            label: "Change Pricing",
            variant: "contained",
            color: "accessory",
            onClick: () => {
              if (row.kind === "tenant") {
                onChangePricing({
                  kind: "tenant",
                  tenantId: row.tenantId,
                  currentBundleId: row.pricingBundleId,
                  tenantName: row.tenantName,
                });
              } else {
                onChangePricing({
                  kind: "subaccount",
                  subaccountId: row.subaccountId,
                  tenantId: row.parentTenantId,
                  currentBundleId: row.pricingBundleId,
                  subaccountName: row.subaccountName,
                  subaccountSlug: row.subaccountSlug,
                  isWeText: row.isWeText,
                });
              }
            },
          },
        ]}
        totalCount={totalCount}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={newSize => {
          onChangePageSize(newSize);
          onChangePage(0);
        }}
        page={page}
        onChangePage={onChangePage}
        emptyState="No active pricing assignments found"
      />
    </TableBox>
  );
}
